import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { 
  trackSchoolSelection, 
  trackStepStart, 
  trackStepCompleted, 
  trackEvaluationRequested, 
  trackEvaluationCompleted, 
  trackNavigationAction, 
  trackUserJourney 
} from '../config/firebase';

// Initial state
const initialState = {
  // Current step in the process
  currentStep: 1,
  
  // Selected school
  selectedSchool: null,
  
  // User data for each step
  demographics: {},
  academics: {},
  resume: {},
  essays: {},
  goals: {},
  additional: {},
  
  // Evaluation results for each step
  evaluations: {
    step1: null,
    step2: null,
    step3: null
  },
  
  // UI state
  isLoading: false,
  errors: {},
  
  // Progress tracking
  completedSteps: [],
  canProceed: false,
  
  // Analytics tracking
  sessionStartTime: Date.now(),
  stepStartTimes: {}
};

// Action types
const ActionTypes = {
  SET_STEP: 'SET_STEP',
  LOAD_DEMO_DATA: 'LOAD_DEMO_DATA',
  SET_SCHOOL: 'SET_SCHOOL',
  UPDATE_DEMOGRAPHICS: 'UPDATE_DEMOGRAPHICS',
  UPDATE_ACADEMICS: 'UPDATE_ACADEMICS',
  UPDATE_RESUME: 'UPDATE_RESUME',
  UPDATE_ESSAYS: 'UPDATE_ESSAYS',
  UPDATE_GOALS: 'UPDATE_GOALS',
  UPDATE_ADDITIONAL: 'UPDATE_ADDITIONAL',
  SET_EVALUATION: 'SET_EVALUATION',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  MARK_STEP_COMPLETE: 'MARK_STEP_COMPLETE',
  RESET_APPLICATION: 'RESET_APPLICATION',
  START_STEP_TIMER: 'START_STEP_TIMER',
  LOAD_FROM_STORAGE: 'LOAD_FROM_STORAGE'
};

// Reducer function
function applicationReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_STEP:
      return {
        ...state,
        currentStep: action.payload
      };
      
    case ActionTypes.SET_SCHOOL:
      return {
        ...state,
        selectedSchool: action.payload
      };
      
    case ActionTypes.UPDATE_DEMOGRAPHICS:
      return {
        ...state,
        demographics: { ...state.demographics, ...action.payload }
      };
      
    case ActionTypes.UPDATE_ACADEMICS:
      return {
        ...state,
        academics: { ...state.academics, ...action.payload }
      };
      
    case ActionTypes.UPDATE_RESUME:
      return {
        ...state,
        resume: { ...state.resume, ...action.payload }
      };
      
    case ActionTypes.UPDATE_ESSAYS:
      return {
        ...state,
        essays: { ...state.essays, ...action.payload }
      };
      
    case ActionTypes.UPDATE_GOALS:
      return {
        ...state,
        goals: { ...state.goals, ...action.payload }
      };
      
    case ActionTypes.UPDATE_ADDITIONAL:
      return {
        ...state,
        additional: { ...state.additional, ...action.payload }
      };
      
    case ActionTypes.SET_EVALUATION:
      return {
        ...state,
        evaluations: {
          ...state.evaluations,
          [`step${action.payload.step}`]: action.payload
        }
      };
      
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
      
    case ActionTypes.SET_ERROR:
      return {
        ...state,
        errors: { ...state.errors, [action.payload.key]: action.payload.message }
      };
      
    case ActionTypes.CLEAR_ERROR:
      const { [action.payload]: removed, ...remainingErrors } = state.errors;
      return {
        ...state,
        errors: remainingErrors
      };
      
    case ActionTypes.MARK_STEP_COMPLETE:
      const updatedSteps = [...state.completedSteps];
      if (!updatedSteps.includes(action.payload)) {
        updatedSteps.push(action.payload);
      }
      return {
        ...state,
        completedSteps: updatedSteps
      };
      
    case ActionTypes.RESET_APPLICATION:
      return {
        ...initialState,
        sessionStartTime: Date.now()
      };
    
    case ActionTypes.START_STEP_TIMER:
      return {
        ...state,
        stepStartTimes: {
          ...state.stepStartTimes,
          [action.payload]: Date.now()
        }
      };
      
    case ActionTypes.LOAD_FROM_STORAGE:
      return {
        ...state,
        ...action.payload,
        sessionStartTime: Date.now() // Reset session time on load
      };
      
    case ActionTypes.LOAD_DEMO_DATA:
      return {
        ...state,
        currentStep: 3, // Jump to essays step
        demographics: {
          firstName: 'Alex',
          lastName: 'Thompson',
          email: 'alex.thompson@email.com',
          phone: '555-123-4567',
          dateOfBirth: '1995-03-15',
          citizenship: 'US',
          ethnicity: 'Asian American',
          gender: 'Non-binary'
        },
        academics: {
          institution: 'University of California, Berkeley',
          degree: 'Bachelor of Science',
          major: 'Computer Science',
          gpa: 3.7,
          graduationDate: '2017-05-01',
          gmatScore: 720,
          greScore: '',
          testType: 'gmat'
        },
        resume: {
          content: `ALEX THOMPSON
Software Engineer | Product Manager

WORK EXPERIENCE

Senior Product Manager | Google | 2021 - Present
• Led product development for Google Cloud AI tools, managing $50M+ revenue stream
• Coordinated cross-functional teams of 15+ engineers, designers, and data scientists
• Increased user engagement by 40% through innovative ML-powered features
• Collaborated with enterprise clients including Fortune 500 companies

Software Engineer | Microsoft | 2019 - 2021  
• Developed Azure cloud infrastructure components using Python and C++
• Improved system performance by 25% through algorithm optimization
• Mentored 3 junior engineers and led technical design reviews
• Contributed to open-source projects with 10k+ GitHub stars

Junior Software Developer | Startup Inc | 2017 - 2019
• Built full-stack web applications using React, Node.js, and PostgreSQL
• Worked in agile development environment with rapid iteration cycles
• Gained experience in early-stage startup operations and product development

EDUCATION
Bachelor of Science in Computer Science | UC Berkeley | 2017
GPA: 3.7/4.0 | Dean's List (2 semesters)

SKILLS
Programming: Python, JavaScript, Java, C++, SQL
Technologies: AWS, Docker, Kubernetes, React, Node.js
Methodologies: Agile, Scrum, Product Management, Data Analysis`,
          fileName: 'alex_thompson_resume.txt',
          fileType: 'text'
        },
        completedSteps: [1, 2] // Mark first two steps as complete
      };
      
    default:
      return state;
  }
}

// Create context
const ApplicationContext = createContext();

// Custom hook to use the context
export const useApplication = () => {
  const context = useContext(ApplicationContext);
  if (!context) {
    throw new Error('useApplication must be used within an ApplicationProvider');
  }
  return context;
};

// Provider component
export const ApplicationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(applicationReducer, initialState);
  
  // Load from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('mba-application-state');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        dispatch({ type: ActionTypes.LOAD_FROM_STORAGE, payload: parsedState });
      } catch (error) {
        console.error('Failed to load saved application state:', error);
      }
    }
  }, []);
  
  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('mba-application-state', JSON.stringify(state));
  }, [state]);
  
  // Helper function to get step name
  const getStepName = (stepNumber) => {
    const stepNames = {
      1: 'Demographics & Academics',
      2: 'Work Experience',
      3: 'Essays & Goals'
    };
    return stepNames[stepNumber] || 'Unknown Step';
  };

  // Action creators with analytics
  const setStep = (stepNumber) => {
    const fromStep = state.currentStep;
    const toStep = stepNumber;
    
    // Track step completion if moving forward
    if (fromStep < toStep && state.stepStartTimes[fromStep]) {
      const duration = (Date.now() - state.stepStartTimes[fromStep]) / 1000;
      trackStepCompleted(fromStep, getStepName(fromStep), duration);
    }
    
    // Track navigation action
    trackNavigationAction(
      toStep > fromStep ? 'next' : 'back',
      fromStep,
      toStep
    );
    
    // Start timer for new step
    trackStepStart(toStep, getStepName(toStep));
    dispatch({ type: ActionTypes.START_STEP_TIMER, payload: toStep });
    dispatch({ type: ActionTypes.SET_STEP, payload: stepNumber });
  };

  const setSchool = (school) => {
    // Only track if school is actually changing
    if (!state.selectedSchool || state.selectedSchool.value !== school.value) {
      trackSchoolSelection(school.value);
    }
    dispatch({ type: ActionTypes.SET_SCHOOL, payload: school });
  };

  const updateDemographics = (data) => {
    dispatch({ type: ActionTypes.UPDATE_DEMOGRAPHICS, payload: data });
  };

  const updateAcademics = (data) => {
    dispatch({ type: ActionTypes.UPDATE_ACADEMICS, payload: data });
  };

  const updateResume = (data) => {
    dispatch({ type: ActionTypes.UPDATE_RESUME, payload: data });
  };

  const updateEssays = (data) => {
    dispatch({ type: ActionTypes.UPDATE_ESSAYS, payload: data });
  };

  const updateGoals = (data) => {
    dispatch({ type: ActionTypes.UPDATE_GOALS, payload: data });
  };

  const updateAdditional = (data) => {
    dispatch({ type: ActionTypes.UPDATE_ADDITIONAL, payload: data });
  };

  const setEvaluation = (evaluation) => {
    // Track evaluation completion
    if (state.stepStartTimes[evaluation.step]) {
      const duration = (Date.now() - state.stepStartTimes[evaluation.step]) / 1000;
      trackEvaluationCompleted(
        evaluation.step,
        getStepName(evaluation.step),
        evaluation.rating,
        duration
      );
    }
    
    dispatch({ type: ActionTypes.SET_EVALUATION, payload: evaluation });
  };

  const setLoading = (isLoading) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: isLoading });
  };

  const setError = (key, message) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: { key, message } });
  };

  const clearError = (key) => {
    dispatch({ type: ActionTypes.CLEAR_ERROR, payload: key });
  };

  const markStepComplete = (stepNumber) => {
    dispatch({ type: ActionTypes.MARK_STEP_COMPLETE, payload: stepNumber });
  };

  const resetToSchoolSelection = () => {
    // Track user journey before reset
    const totalTime = (Date.now() - state.sessionStartTime) / 1000 / 60; // in minutes
    trackUserJourney(state.completedSteps.length, totalTime);
    
    trackNavigationAction('start_over', state.currentStep, 1);
    dispatch({ type: ActionTypes.RESET_APPLICATION });
  };

  const loadDemoData = () => {
    dispatch({ type: ActionTypes.LOAD_DEMO_DATA });
    trackNavigationAction('demo_loaded', state.currentStep, 3);
  };

  // Navigation helpers
  const canGoBack = () => {
    return state.currentStep > 1;
  };

  const goBack = () => {
    if (canGoBack()) {
      setStep(state.currentStep - 1);
    }
  };

  const getCandidateData = () => {
    return {
      selectedSchool: state.selectedSchool?.value,
      demographics: state.demographics,
      academics: state.academics,
      resume: state.resume,
      essays: state.essays,
      goals: state.goals,
      additional: state.additional
    };
  };

  // Initialize step timer on mount
  useEffect(() => {
    trackStepStart(1, getStepName(1));
    dispatch({ type: ActionTypes.START_STEP_TIMER, payload: 1 });
  }, []);

  const value = {
    // State
    currentStep: state.currentStep,
    selectedSchool: state.selectedSchool,
    demographics: state.demographics,
    academics: state.academics,
    resume: state.resume,
    essays: state.essays,
    goals: state.goals,
    additional: state.additional,
    evaluations: state.evaluations,
    isLoading: state.isLoading,
    errors: state.errors,
    completedSteps: state.completedSteps,
    canProceed: state.canProceed,
    
    // Actions
    setStep,
    setSchool,
    updateDemographics,
    updateAcademics,
    updateResume,
    updateEssays,
    updateGoals,
    updateAdditional,
    setEvaluation,
    setLoading,
    setError,
    clearError,
    markStepComplete,
    resetToSchoolSelection,
    loadDemoData,
    
    // Navigation
    canGoBack,
    goBack,
    getCandidateData,
    
    // Analytics helpers
    trackEvaluationRequested: (stepNumber) => {
      trackEvaluationRequested(stepNumber, getStepName(stepNumber));
    }
  };
  
  return (
    <ApplicationContext.Provider value={value}>
      {children}
    </ApplicationContext.Provider>
  );
}; 